import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, fmtDateTime } from "@/lib/date";
import { STAGES, STATUSES } from "@/lib/constants";
import { StageBadge, StatusBadge } from "@/components/Badges";
import ScopeSwitch from "@/components/ScopeSwitch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Plus, X } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const ALL = "__all__";

export default function PBOList() {
  const { scopes, isViewAs, user } = useAuth();
  const canPickTeam = scopes?.includes("all");
  const [scope, setScope] = useState(null);
  const [pbos, setPbos] = useState([]);
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [team, setTeam] = useState(ALL);
  const [stage, setStage] = useState(ALL);
  const [statusF, setStatusF] = useState(ALL);

  useEffect(() => {
    api.get("/reference").then((r) => setRef(r.data));
  }, []);

  useEffect(() => {
    // Identity changed (login / View As enter, exit, switch): drop stale scope, filters and results.
    setScope(null); setPbos([]); setTeam(ALL); setStage(ALL); setStatusF(ALL); setQ("");
  }, [user?.id]);

  useEffect(() => {
    if (!scopes?.length) return;
    if (!scope) setScope(scopes.includes("all") ? "all" : (scopes.includes("team") ? "team" : "personal"));
  }, [scopes, scope]);

  useEffect(() => {
    if (!scope) return;
    setLoading(true);
    const params = { scope };
    if (q) params.q = q;
    if (canPickTeam && team !== ALL) params.team = team;
    if (stage !== ALL) params.stage = stage;
    if (statusF !== ALL) params.status = statusF;
    api.get("/pbos", { params }).then((r) => setPbos(r.data)).finally(() => setLoading(false));
  }, [q, team, stage, statusF, scope, canPickTeam]);

  const teams = ref?.teams || [];
  const hasFilters = q || team !== ALL || stage !== ALL || statusF !== ALL;
  const clear = () => { setQ(""); setTeam(ALL); setStage(ALL); setStatusF(ALL); };

  return (
    <div className="space-y-5" data-testid="pbo-list-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Pipeline</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">PBOs</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ScopeSwitch value={scope} onChange={setScope} scopes={scopes} />
          {!isViewAs && (
            <Link to="/pbos/new">
              <Button data-testid="new-pbo-btn" className="rounded-full bg-[#0A1F44] hover:bg-[#0A1F44]/90 text-white h-10">
                <Plus className="w-4 h-4 mr-1" /> Add PBO
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="pbo-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or partner…" className="pl-9 h-11 bg-white" />
        </div>
        <div className={`grid gap-2 ${canPickTeam ? "grid-cols-3" : "grid-cols-2"}`}>
          {canPickTeam && <FilterSelect testId="filter-team" value={team} onChange={setTeam} label="All teams" options={teams} />}
          <FilterSelect testId="filter-stage" value={stage} onChange={setStage} label="All stages" options={STAGES} />
          <FilterSelect testId="filter-status" value={statusF} onChange={setStatusF} label="All statuses" options={STATUSES} />
        </div>
        {hasFilters && (
          <button data-testid="clear-filters" onClick={clear} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>

      <div className="space-y-2" data-testid="pbo-list">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && pbos.length === 0 && (
          <div className="p-8 text-center bg-white border border-dashed border-slate-200 rounded-xl">
            <div className="text-slate-500 text-sm">No PBOs match your filters.</div>
          </div>
        )}
        {pbos.map((p) => (
          <Link key={p.id} to={`/pbos/${p.id}`} data-testid={`pbo-row-${p.id}`}
            className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-[#0A1F44]/40 hover:shadow-sm transition">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium text-slate-900 truncate">{p.name}</div>
                  {p.partner_name && <div className="text-xs text-slate-500">& {p.partner_name}</div>}
                  {p.is_demo && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">demo</span>}
                </div>
                <div className="text-xs text-slate-500 mt-1 truncate">
                  IBO/Partner: <span className="text-slate-700">{p.ibo_partner_name || "—"}</span>
                  {p.upline_team && <> · {p.upline_team}</>}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <StageBadge stage={p.current_stage} />
                <StatusBadge status={p.status} />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] text-slate-500">
              <div>Entered <span className="text-slate-700">{fmtDate(p.date_entered)}</span></div>
              <div className="text-right">
                {p.status === "Launched" && <>Launched <span className="text-slate-700">{fmtDate(p.launch_date)}</span></>}
                {p.status === "Dropped Out" && <>Dropped <span className="text-slate-700">{fmtDate(p.dropout_date)}</span></>}
                {p.status === "Active" && p.next_meeting?.at && <>Next <StageBadge stage={p.next_meeting.stage} /> <span className="text-slate-700 ml-1">{fmtDateTime(p.next_meeting.at)}</span></>}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, label, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId} className="h-10 bg-white"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
